import { useEffect, useState } from 'react';
import type { AccountSlugRefusal } from 'fym-shared/src/music24/accountSlug';
import { useVerifyAccountNameSlugMutation } from '../../../redux/api/fymAPI';
import { slugRefusalOf } from './accountSlugRefusalText';

export enum AddressState {
  /** Nothing typed, or the address is the one already saved. */
  idle = 'idle',
  checking = 'checking',
  free = 'free',
  /** Another account answers to it. */
  taken = 'taken',
  /** An address that nobody may take: see `refusal` for the reason. */
  illegal = 'illegal',
}

export interface AccountSlugCheck {
  state: AddressState;
  /**
   * The one address the server offers instead. `verifyAccountNameSlug` returns
   * a single `availableSlug`, so there is one — not a menu.
   */
  suggestion?: string;
  /**
   * Why the address cannot be used (`illegal`): too short, too long, bad characters, or kept for
   * Music24 (QA 2026-10-28, brk-r2-4). Null otherwise, and on an older API.
   */
  refusal: AccountSlugRefusal | null;
}

const CHECK_DELAY_MS = 450;

/**
 * The account address, checked while it is typed.
 *
 * The same endpoint the form's submit-time validator uses, moved to every
 * keystroke so the verdict arrives before the artist commits to a name. The
 * account's own id goes with the request, so its current address never comes
 * back as taken.
 */
export function useAccountSlugCheck(slug: string, accountId: number | undefined, savedSlug: string): AccountSlugCheck {
  const [verifySlug] = useVerifyAccountNameSlugMutation();
  const [state, setState] = useState(AddressState.idle);
  const [suggestion, setSuggestion] = useState<string>();
  const [refusal, setRefusal] = useState<AccountSlugRefusal | null>(null);

  useEffect(() => {
    if (slug === '' || slug === savedSlug) {
      setState(AddressState.idle);
      setSuggestion(undefined);
      setRefusal(null);
      return undefined;
    }
    setState(AddressState.checking);
    setRefusal(null);
    const timer = setTimeout(() => {
      verifySlug({ slug, accountId })
        .unwrap()
        .then((result) => {
          const refused = slugRefusalOf(result);
          // An address that nobody may take gets no "free right now" offer (QA brk-r2-4): the
          // offer is only for an address that another artist has.
          if (refused !== null || !result.isValid) {
            setSuggestion(undefined);
            setRefusal(refused ?? 'characters');
            setState(AddressState.illegal);
            return;
          }
          setSuggestion(result.isAvailable ? undefined : result.availableSlug);
          setState(result.isAvailable ? AddressState.free : AddressState.taken);
        })
        // A check we could not make is not a verdict. The save call validates
        // again, and refuses the address there if this one was wrong.
        .catch(() => setState(AddressState.idle));
    }, CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [accountId, savedSlug, slug, verifySlug]);

  return { state, suggestion, refusal };
}
